// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { productCarouselProductStyles } from "@hardwoods/Overrides/Components/ProductCarouselProduct";
import ProductImage, { ProductImageStyles } from "@hardwoods/Overrides/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import { partNumberStyle } from "@hardwoods/Overrides/Widgets/ProductList/helper";
import { ellipsis } from "@hardwoods/XDL/Utilities";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
    };
};

interface OwnProps {
    widgetId: string;
    product: ProductModel;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ShopByProductStyles {
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    infoSection?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productBrandWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPriceStyles?: ProductPriceStyles;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    brandName?: TypographyProps;
    priceWrapper?: TypographyProps;
}

export const shopByProduct: ShopByProductStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding: 15px;
            width: 100%;
            height: 100%;
            border: 1px solid ${color("common.border")};
            border-radius: 4px;
            background-color: ${color("common.white")};
            ${xScreen.lte("sm")} {
                flex-direction: column;
            }
            ${xScreen.lte("xs")} {
                max-width: 352px;
            }
        `,
    },
    leftSection: {
        css: css`
            margin-right: 15px;
            ${xScreen.lte("sm")} {
                margin-right: 0;
                margin-bottom: 15px;
            }
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            flex-grow: 1;
            flex-direction: column;
            justify-content: space-between;
            ${xScreen.lte("xs")} {
                margin-top: 15px;
                margin-left: 0;
            }
        `,
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                ${xScreen.lte("sm")} {
                    justify-content: center;
                }
            `,
        },
        image: {
            css: css`
                height: 116px;
                width: 116px;

                img {
                    height: 100%;
                }
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                margin-bottom: 5px;
            `,
        },
        erpNumberLabelText: {
            ...partNumberStyle.label,
        },
        erpNumberValueText: {
            ...partNumberStyle.value,
        },
    },
    productBrandWrapper: {
        css: css`
            margin-bottom: 5px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 14,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 18px;
                    ${ellipsis(3)}
                `,
            },
            css: css`
                margin-bottom: 25px;
            `,
        },
    },
    brandName: {
        size: 12,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
};

const ShopByProduct: React.FC<Props> = ({ product, productInfo, widgetId }) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, shopByProduct));

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
    };

    const hasSamePrices =
        productContext?.productInfo?.pricing?.additionalResults?.priceUOMNetPrice ===
        productContext?.productInfo?.pricing?.unitNetPriceDisplay;

    const isVariantParent = productContext?.product?.isVariantParent;

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftSection}>
                    <ProductImage
                        product={productContext}
                        imageTransforms={{ width: 116, quality: "good" }}
                        extendedStyles={styles.productImageStyles}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <StyledWrapper {...styles.infoSection}>
                        {product.brand && (
                            <StyledWrapper {...styles.productBrandWrapper}>
                                <Typography {...styles.brandName} data-test-selector="brandName">
                                    {product.brand.name}
                                </Typography>
                            </StyledWrapper>
                        )}
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showCustomerName={true}
                            showManufacturerItem={false}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceWrapper}>
                        <VisuallyHidden as="label" htmlFor={`${widgetId}-uom`} id={`${widgetId}-uom-label`}>
                            {translate("U/M")}
                        </VisuallyHidden>
                        {!hasSamePrices && (
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                showPriceUOM={true}
                                extendedStyles={styles.productPriceStyles}
                            />
                        )}
                        <StyledWrapper className={hasSamePrices ? "hasSamePrices" : ""}>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                showPriceUOM={!!isVariantParent}
                                showSignIn={true}
                                extendedStyles={styles.productPriceStyles}
                            />
                        </StyledWrapper>
                        <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps)(ShopByProduct);
